import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Logo } from '../common/Logo';
import { headerFor } from './headerTokens';
import { NavIcon } from './navIcons';

/** One step of the trail. A step with `onActivate` is a link; the step without one is the current page. */
export interface TrailStep {
  label: string;
  onActivate?: () => void;
}

/**
 * The breadcrumb (handoff sections 8 and 9). Earlier steps are links in the muted grey that turn
 * link-blue on hover; the current page, when shown, is ink at 600. A chevron follows every link and
 * takes the trail's colour.
 *
 * `size` 13 is the project trail; 12 is the toolbar's line over the page title, which shows the
 * trail minus its last step.
 */
export function TrailCrumbs(props: { steps: readonly TrailStep[]; size: 12 | 13 }) {
  const { t } = useTranslation();
  const H = headerFor(useTheme());
  return (
    <Box
      component="nav"
      aria-label={t('nav:rail.trailLabel')}
      data-testid="page-trail"
      sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', fontSize: props.size, fontWeight: 500, color: H.muted }}
    >
      {props.steps.map((step, i) => (
        <Box key={`${i}-${step.label}`} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
          {step.onActivate ? (
            <>
              <Box
                component="button"
                type="button"
                onClick={step.onActivate}
                sx={{ all: 'unset', cursor: 'pointer', borderRadius: '4px', color: H.muted, '&:hover': { color: H.link } }}
              >
                {step.label}
              </Box>
              <NavIcon name="chevronRight" size={12} strokeWidth={2} />
            </>
          ) : (
            <Box component="span" aria-current="page" sx={{ color: H.ink, fontWeight: 600 }}>
              {step.label}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}

/**
 * The project trail row: the wordmark group and the full trail on one row, with no header band above
 * it (handoff section 9).
 *
 * The row is the first child of the workspace container, which sits 10px right of the page edge (the
 * rail's sliver) inside 16px of padding. The design's wordmark is `left: 24px; top: 10px` from the
 * page's top-left, so measured from the row it is `left: -2px; top: -6px`, and the trail's 122px
 * left padding leaves it clear of the wordmark group. `wordmark` is false where the shell already
 * carries the brand (the phone header), and the row then drops the wordmark and its padding.
 */
export function ProjectTrailRow(props: {
  steps: readonly TrailStep[];
  onHome: () => void;
  wordmark?: boolean;
}) {
  const { t } = useTranslation();
  const H = headerFor(useTheme());
  const P = H.project;
  const wordmark = props.wordmark ?? true;
  return (
    <Box sx={{ position: 'relative', flex: 'none', pl: wordmark ? `${P.trailPaddingLeft}px` : 0, mb: `${P.trailMarginBottom}px`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
      {wordmark && (
        <Box
          component="button"
          type="button"
          onClick={props.onHome}
          aria-label={t('nav:header.home')}
          data-testid="project-wordmark"
          sx={{
            all: 'unset',
            position: 'absolute',
            left: P.wordmarkLeft,
            top: P.wordmarkTop,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            width: 'max-content',
            borderRadius: '8px',
          }}
        >
          <Box
            sx={{
              width: P.tile,
              height: P.tile,
              borderRadius: P.tileRadius,
              background: P.tileBackground,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 'none',
              boxShadow: P.tileShadow,
            }}
          >
            <Logo size={P.tileMark} />
          </Box>
          <Box component="span" sx={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px', color: H.ink }}>
            {t('nav:header.appName')}
          </Box>
          <Box component="span" aria-hidden sx={{ width: '1px', height: 22, background: P.dividerColor, ml: '8px' }} />
        </Box>
      )}
      <TrailCrumbs steps={props.steps} size={13} />
    </Box>
  );
}
